/* fitment-order.v2.js
   Vehicles first; universals appended at the very end (Ignite + <infinite-scroll> compatible).
   - Looks for <infinite-scroll> / .js-pagination as the main container.
   - Hides any item that contains [data-fitment="universal"] until the end.
   - Buffers universals during infinite scroll; flushes when no more pages OR when your infinite-scroll announces end.
*/
(() => {
  const DEBUG = false;

  // ----- Container / List detection -----
  function findContainer() {
    return document.querySelector('infinite-scroll')
        || document.querySelector('.js-pagination')
        || document.querySelector('.product-grid')
        || document.querySelector('.collection .grid')
        || document.querySelector('.grid.product-grid')
        || document.querySelector('[data-section-type="collection-product-grid"] .grid')
        || document.querySelector('.grid, ul[role="list"]');
  }

  const container = findContainer();
  if (!container) return;

  function findListRoot() {
    // Where items actually live:
    return (container.matches('.js-pagination') ? container : null)
        || container.querySelector('.js-pagination')
        || container.querySelector('.product-grid, .grid, ul[role="list"]')
        || container;
  }

  let listRoot = findListRoot();

  // ----- CSS: prevent flash of universal items (hide wrappers using :has) -----
  const style = document.createElement('style');
  style.textContent = String.raw`
  .fitment-universal-hidden [data-fitment="universal"]{display:none !important;}
  .fitment-universal-hidden .js-pagination__item:has([data-fitment="universal"]){display:none !important;}
  .fitment-universal-hidden .grid__item:has([data-fitment="universal"]){display:none !important;}
  .fitment-universal-hidden li:has([data-fitment="universal"]){display:none !important;}
  `;
  document.head.appendChild(style);

  // Add the class to the element that wraps the list
  (listRoot || container).classList.add('fitment-universal-hidden');

  // ----- Hidden buffer for universals -----
  const buffer = document.createElement('div');
  buffer.id = 'fitment-universal-buffer';
  buffer.style.display = 'none';
  (listRoot || container).after(buffer);

  // ----- Utilities -----
  const BLOCK_SELECTORS = '.js-pagination__item, .grid__item, .product-card-wrapper, .card-wrapper, li';

  function cardBlock(node) {
    if (!(node instanceof Element)) return null;
    return node.closest(BLOCK_SELECTORS) || node;
  }

  function processFitmentEl(el) {
    if (!el || !(el instanceof Element)) return;
    if (el.__fitmentProcessed) return;
    const type = el.getAttribute('data-fitment');
    if (!type) return;
    const block = cardBlock(el);
    if (!block) return;
    el.__fitmentProcessed = true;

    if (type === 'universal') {
      if (block.parentElement !== buffer) {
        buffer.appendChild(block);
        if (DEBUG) console.log('[fitment] buffered universal:', block);
      }
    } else {
      // Vehicle-specific stays; ensure visible if previously hidden
      block.style.removeProperty('display');
    }
  }

  function scan(root) {
    if (!root) return;
    if (root instanceof Element && root.hasAttribute('data-fitment')) processFitmentEl(root);
    root.querySelectorAll?.('[data-fitment]').forEach(processFitmentEl);
  }

  // Initial pass
  scan(listRoot || container);

  // ----- Mutation observers (for infinite scroll appends) -----
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((n) => {
        if (n.nodeType !== 1) return;
        scan(n);
      });
    }
    maybeFlush();
  });

  // Observe where items get appended
  mo.observe(listRoot || container, { childList: true, subtree: true });

  // Also watch for listRoot being removed/changed (some themes replace it)
  const containerObserver = new MutationObserver(() => {
    const newList = findListRoot();
    if (newList !== listRoot) {
      if (DEBUG) console.log('[fitment] list root changed');
      listRoot = newList;
      mo.disconnect();
      mo.observe(listRoot || container, { childList: true, subtree: true });
    }
    maybeFlush();
  });
  containerObserver.observe(document.body, { childList: true, subtree: true });

  // ----- Flushing logic -----
  let flushed = false;
  let flushScheduled = false;

  function hasNextPage() {
    // Next button inside the container/list
    const nextBtn = (listRoot || container).querySelector('.js-pagination__next-button[href]');
    if (nextBtn) {
      const disabled = nextBtn.getAttribute('aria-disabled') === 'true'
                    || nextBtn.classList.contains('is-disabled')
                    || nextBtn.classList.contains('disabled')
                    || nextBtn.hasAttribute('disabled');
      if (!disabled) return true;
    }

    // Shopify rel=next in head (present on first-page; may disappear at end in some setups)
    if (document.querySelector('link[rel="next"]')) return true;

    // If no pagination container exists anymore, assume no next page
    if (!(document.querySelector('.js-pagination'))) return false;

    // Default: no next
    return false;
  }

  function flushUniversals() {
    if (flushed) return;
    const target = findListRoot();
    if (DEBUG) console.log('[fitment] flushing universals →', buffer.childElementCount, 'items');
    while (buffer.firstChild) target.appendChild(buffer.firstChild);
    (target || container).classList.remove('fitment-universal-hidden');
    flushed = true;
    mo.disconnect();
    containerObserver.disconnect();
  }

  function maybeFlush() {
    if (flushed) return;
    if (!hasNextPage()) {
      if (!flushScheduled) {
        flushScheduled = true;
        setTimeout(flushUniversals, 0);
      }
    }
  }

  // Listen for explicit end-of-results events (your infinite-scroll now fires these)
  window.addEventListener('collection:end-of-results', flushUniversals);
  window.addEventListener('ignite:infinite:end', flushUniversals);
  window.addEventListener('theme:collection:end', flushUniversals);

  // Final safety: if the user reaches bottom and there's no visible next control, flush
  let t;
  window.addEventListener('scroll', () => {
    if (flushed) return;
    if (window.innerHeight + window.scrollY + 200 >= document.body.offsetHeight) {
      clearTimeout(t);
      t = setTimeout(() => {
        const anyNext = (findListRoot() || container).querySelector('.js-pagination__next-button[href]');
        if (!anyNext) flushUniversals();
      }, 1000);
    }
  }, { passive: true });

  if (DEBUG) {
    console.log('[fitment] init; listRoot=', listRoot);
    console.log('[fitment] vehicles now:', document.querySelectorAll('[data-fitment="vehicle"]').length);
    console.log('[fitment] universals buffered:', buffer.childElementCount);
  }
})();
